import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import {
  Plus,
  Send,
  Mail,
  Twitter,
  MessageSquare,
  MoreHorizontal,
  Trash2,
  Pause,
  Play,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

const ChannelIcon = ({ type }: { type: string }) => {
  switch (type) {
    case "Telegram":
      return <Send className="w-3.5 h-3.5" />;
    case "Email":
      return <Mail className="w-3.5 h-3.5" />;
    case "Twitter":
      return <Twitter className="w-3.5 h-3.5" />;
    case "Discord":
      return <MessageSquare className="w-3.5 h-3.5" />;
    default:
      return <Send className="w-3.5 h-3.5" />;
  }
};

export default function Broadcasts() {
  const rules = useQuery(api.rules.list) || [];
  const channels = useQuery(api.channels.list) || [];
  const createRule = useMutation(api.rules.create);
  const updateRule = useMutation(api.rules.update);
  const removeRule = useMutation(api.rules.remove);
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    frequency: "hourly",
    jobType: "Any",
    minRate: "",
    keywords: "",
    selectedChannels: [] as string[],
  });

  const isLoading = rules === undefined || channels === undefined;

  const resetForm = () => {
    setFormData({
      name: "",
      frequency: "hourly",
      jobType: "Any",
      minRate: "",
      keywords: "",
      selectedChannels: [],
    });
  };

  const handleCreateRule = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a rule name",
        variant: "destructive",
      });
      return;
    }

    if (formData.selectedChannels.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one channel",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const criteria = {
        jobType: formData.jobType,
        minRate: formData.minRate
          ? parseFloat(formData.minRate.replace(/[^0-9.]/g, ""))
          : undefined,
        keywords: formData.keywords
          ? formData.keywords
              .split(",")
              .map((k) => k.trim())
              .filter(Boolean)
          : [],
      };

      const criteriaDisplay =
        [
          formData.jobType !== "Any" ? formData.jobType : null,
          formData.minRate ? `Min $${formData.minRate}/hr` : null,
          formData.keywords ? formData.keywords : null,
        ]
          .filter(Boolean)
          .join(" • ") || "All Jobs";

      await createRule({
        name: formData.name,
        frequency: formData.frequency,
        channels: formData.selectedChannels,
        criteria,
        criteriaDisplay,
      });

      toast({
        title: "Rule created",
        description: `"${formData.name}" has been created successfully`,
      });

      resetForm();
      setIsDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "Failed to create rule",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleRuleStatus = async (
    ruleId: string,
    currentStatus: string
  ) => {
    try {
      const newStatus = currentStatus === "Active" ? "Paused" : "Active";
      await updateRule({
        id: ruleId as any,
        status: newStatus,
      });

      toast({
        title: newStatus === "Active" ? "Rule activated" : "Rule paused",
      });
    } catch (error: any) {
      toast({
        title: "Failed to update rule",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteRule = async (ruleId: string, ruleName: string) => {
    if (!confirm(`Are you sure you want to delete "${ruleName}"?`)) {
      return;
    }

    try {
      await removeRule({ id: ruleId as any });
      toast({
        title: "Rule deleted",
        description: `"${ruleName}" has been deleted`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to delete rule",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleManageChannels = () => {
    setLocation("/settings");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold tracking-tight">Broadcast Rules</h1>
          <p className="text-sm text-muted-foreground">
            Manage automated job alerts and digests.
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-2">
              <Plus className="w-4 h-4" /> New Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create Broadcast Rule</DialogTitle>
              <DialogDescription>
                Configure a new automated alert schedule.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right text-xs">
                  Name
                </Label>
                <Input
                  id="name"
                  placeholder="e.g. Daily Python Jobs"
                  className="col-span-3 h-8"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="frequency" className="text-right text-xs">
                  Frequency
                </Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(value) =>
                    setFormData({ ...formData, frequency: value })
                  }
                >
                  <SelectTrigger className="col-span-3 h-8">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="realtime">
                      Real-time (Immediate)
                    </SelectItem>
                    <SelectItem value="hourly">Hourly Digest</SelectItem>
                    <SelectItem value="daily">Daily Digest</SelectItem>
                    <SelectItem value="weekly">Weekly Summary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="text-right text-xs mt-2">Criteria</Label>
                <div className="col-span-3 space-y-2 border border-border rounded-md p-3 bg-secondary/10">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Job Type
                    </span>
                    <select
                      className="text-xs bg-transparent border-none focus:ring-0 font-medium text-right"
                      value={formData.jobType}
                      onChange={(e) =>
                        setFormData({ ...formData, jobType: e.target.value })
                      }
                    >
                      <option>Any</option>
                      <option>Hourly Only</option>
                      <option>OTE Only</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Min Rate
                    </span>
                    <input
                      className="w-20 h-6 text-xs bg-background border border-border rounded px-2 text-right"
                      placeholder="$20"
                      value={formData.minRate}
                      onChange={(e) =>
                        setFormData({ ...formData, minRate: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Keywords
                    </span>
                    <input
                      className="w-32 h-6 text-xs bg-background border border-border rounded px-2 text-right"
                      placeholder="React, Node..."
                      value={formData.keywords}
                      onChange={(e) =>
                        setFormData({ ...formData, keywords: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="channel" className="text-right text-xs">
                  Channel
                </Label>
                <Select
                  value={formData.selectedChannels[0] || ""}
                  onValueChange={(value) =>
                    setFormData({ ...formData, selectedChannels: [value] })
                  }
                >
                  <SelectTrigger className="col-span-3 h-8">
                    <SelectValue placeholder="Select destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.length === 0 ? (
                      <SelectItem value="none" disabled>
                        No channels configured
                      </SelectItem>
                    ) : (
                      channels.map((c) => (
                        <SelectItem key={c._id} value={c._id}>
                          {c.name} ({c.type})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDialogOpen(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                onClick={handleCreateRule}
                disabled={isSaving}
              >
                {isSaving ? "Creating..." : "Create Rule"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {rules.length === 0 ? (
            <div className="rounded-md border border-border p-12 text-center bg-card">
              <p className="text-sm text-muted-foreground mb-2">
                No broadcast rules configured
              </p>
              <p className="text-xs text-muted-foreground">
                Create your first rule to automate job notifications
              </p>
            </div>
          ) : (
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead className="bg-secondary/50 text-xs uppercase text-muted-foreground font-medium">
                  <tr>
                    <th className="px-4 py-3">Rule Name</th>
                    <th className="px-4 py-3">Frequency</th>
                    <th className="px-4 py-3">Criteria</th>
                    <th className="px-4 py-3">Channels</th>
                    <th className="px-4 py-3 w-[50px]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {rules.map((rule) => (
                    <tr key={rule._id} className="group hover:bg-secondary/20">
                      <td className="px-4 py-3 font-medium">
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              rule.status === "Active"
                                ? "bg-emerald-500"
                                : "bg-zinc-500"
                            )}
                          />
                          {rule.name}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className="font-normal text-xs"
                        >
                          {rule.frequency}
                        </Badge>
                      </td>
                      <td
                        className="px-4 py-3 text-muted-foreground text-xs max-w-[200px] truncate"
                        title={rule.criteriaDisplay}
                      >
                        {rule.criteriaDisplay}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center -space-x-2">
                          {rule.channels.map((cid) => {
                            const channel = channels.find((c) => c._id === cid);
                            if (!channel) return null;
                            return (
                              <div
                                key={cid}
                                className="w-6 h-6 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground"
                                title={channel.name}
                              >
                                <ChannelIcon type={channel.type} />
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MoreHorizontal className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                handleToggleRuleStatus(rule._id, rule.status)
                              }
                            >
                              {rule.status === "Active" ? (
                                <>
                                  <Pause className="w-4 h-4 mr-2" /> Pause Rule
                                </>
                              ) : (
                                <>
                                  <Play className="w-4 h-4 mr-2" /> Activate
                                  Rule
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() =>
                                handleDeleteRule(rule._id, rule.name)
                              }
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete Rule
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Integrations
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={handleManageChannels}
            >
              Manage
            </Button>
          </div>

          <div className="space-y-3">
            {channels.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No channels configured. Add channels in Settings.
              </div>
            ) : (
              channels.map((channel) => (
                <div
                  key={channel._id}
                  className="flex items-center justify-between p-3 border border-border rounded-md bg-card"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-secondary flex items-center justify-center">
                      <ChannelIcon type={channel.type} />
                    </div>
                    <div>
                      <div className="text-sm font-medium">{channel.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {channel.target}
                      </div>
                    </div>
                  </div>
                  <div
                    className={cn(
                      "w-2 h-2 rounded-full",
                      channel.status === "Active"
                        ? "bg-emerald-500"
                        : channel.status === "Paused"
                          ? "bg-amber-500"
                          : "bg-red-500"
                    )}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
